import { z } from "zod/v4";
import { CameraSubject, defineEndpoint, defineResource, SingleResponse } from "@visioni/a3s-client-api";
import { SiteIdCodec } from "@visioni/a3s-schema/codecs";
import { isoDatetimeToDateString } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";

/**
 * Client-API resource surface for the alarm feature.
 *
 * The underlying domain schemas (`AlarmConfiguration`, `AlarmOverride`,
 * `AlarmSilence`) live in `../schema.ts` and are defined against Zod v3.
 * `defineResource`/`defineEndpoint` only accept Zod v4. Rather than
 * maintain two parallel schemas (the UI contract is already pinned by the
 * v3 types), we use a permissive v4 passthrough schema at the wire edge
 * and rely on the existing v3 parsers downstream when structured access is
 * needed (e.g. `AlarmConfiguration.parse(response.data)`).
 *
 * Scope: these resources exist so Phase 4 Storybook stories can exercise
 * the query/command path (via MSW handlers and, optionally, a live MR
 * environment). The `apps/a3s-app` API layer is unchanged.
 */

/** Passthrough object schema — we don't re-validate the alarm configuration here. */
const PassthroughObject = z.looseObject({});

// #region Resources

export const alarmConfigurationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "alarm-configuration",
});

export const alarmOverrideResource = defineResource({
  feature: FEATURE_NAME,
  resource: "alarm-override",
});

export const alarmSilenceResource = defineResource({
  feature: FEATURE_NAME,
  resource: "alarm-silence",
});

// #endregion

// #region Endpoints

/** GET the current alarm configuration for a camera. */
export const getAlarmConfigurationEndpoint = defineEndpoint(alarmConfigurationResource, {
  responseSchema: PassthroughObject,
});

/** POST a new override (arm/disarm) for a camera. `null` body clears the override. */
export const setAlarmOverrideEndpoint = defineEndpoint(alarmOverrideResource, {
  method: "POST",
  bodySchema: PassthroughObject,
  responseSchema: PassthroughObject,
});

/** DELETE the current override. */
export const clearAlarmOverrideEndpoint = defineEndpoint(alarmOverrideResource, {
  method: "DELETE",
  responseSchema: PassthroughObject,
});

/** POST a new silence for a camera. */
export const setAlarmSilenceEndpoint = defineEndpoint(alarmSilenceResource, {
  method: "POST",
  bodySchema: PassthroughObject,
  responseSchema: PassthroughObject,
});

// #endregion

// #region Schedule History

export const ScheduleHistoryQueryParams = z.object({
  from: isoDatetimeToDateString,
  to: isoDatetimeToDateString,
});

export type ScheduleHistoryQueryParams = z.output<typeof ScheduleHistoryQueryParams>;

export const ScheduleHistoryEntry = z.object({
  schedule: z.looseObject({}),
  activeFrom: z.string(),
  activeUntil: z.string().nullable(),
});

export type ScheduleHistoryEntry = z.output<typeof ScheduleHistoryEntry>;

export const ScheduleHistoryResponseData = z.object({
  subject: CameraSubject,
  entries: z.array(ScheduleHistoryEntry),
  timezone: z.string().nullable(),
});

export type ScheduleHistoryResponseData = z.output<typeof ScheduleHistoryResponseData>;

export const ScheduleHistoryResponse = SingleResponse(ScheduleHistoryResponseData);

export type ScheduleHistoryResponse = z.output<typeof ScheduleHistoryResponse>;

export const scheduleHistoryResource = defineResource({
  feature: FEATURE_NAME,
  resource: "schedule-history",
});

/** GET the history of applied schedules for a camera within a time range. */
export const getScheduleHistoryEndpoint = defineEndpoint(scheduleHistoryResource, {
  responseSchema: ScheduleHistoryResponse,
  querySchema: ScheduleHistoryQueryParams,
});

// #endregion

// #region Site Override

const SiteOverridePathParams = z.object({ siteId: SiteIdCodec });

export const siteAlarmOverrideResource = defineResource({
  feature: FEATURE_NAME,
  resource: "sites/{siteId}/override",
  scope: "tenant",
  resourcePathSchema: SiteOverridePathParams,
});

export const SiteOverrideBody = z.object({
  override: z.looseObject({}).nullable(),
});

export type SiteOverrideBody = z.infer<typeof SiteOverrideBody>;

export const SiteOverrideResponseData = z.object({
  camerasUpdated: z.number().int().nonnegative(),
});

export type SiteOverrideResponseData = z.infer<typeof SiteOverrideResponseData>;

/** POST a site-wide override (arm/disarm all cameras). */
export const setSiteOverrideEndpoint = defineEndpoint(siteAlarmOverrideResource, {
  method: "POST",
  bodySchema: SiteOverrideBody,
  responseSchema: SiteOverrideResponseData,
});

// #endregion
